import React, { createContext, useContext, useState, useCallback } from 'react';
import Toast from '../components/common/Toast';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type: 'info',
      duration: 2000,
      isVisible: false,
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);

    // Trigger animation after a small delay
    setTimeout(() => {
      setToasts(prev => 
        prev.map(t => t.id === id ? { ...t, isVisible: true } : t)
      );
    }, 10);

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    // First trigger exit animation
    setToasts(prev => 
      prev.map(toast => 
        toast.id === id ? { ...toast, isVisible: false } : toast
      )
    );

    // Remove from DOM after animation completes
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 300);
  }, []);

  const clearAllToasts = useCallback(() => {
    // Trigger exit animation for all toasts
    setToasts(prev => prev.map(toast => ({ ...toast, isVisible: false })));
    
    // Remove all toasts after animation
    setTimeout(() => {
      setToasts([]);
    }, 300);
  }, []);

  // Convenience methods for different toast types
  const showSuccess = useCallback((message, options = {}) => {
    return addToast({ 
      type: 'success', 
      message, 
      title: options.title || 'Success',
      ...options 
    });
  }, [addToast]);

  const showError = useCallback((message, options = {}) => {
    return addToast({ 
      type: 'error', 
      message, 
      title: options.title || 'Error',
      duration: options.duration || 3000, // 3 seconds
      ...options 
    });
  }, [addToast]);

  const showWarning = useCallback((message, options = {}) => {
    return addToast({ 
      type: 'warning', 
      message, 
      title: options.title || 'Warning',
      ...options 
    });
  }, [addToast]);

  const showInfo = useCallback((message, options = {}) => {
    return addToast({ 
      type: 'info', 
      message, 
      title: options.title || 'Info',
      ...options 
    });
  }, [addToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      
      {/* Toast Container - Positioned below header */}
      {toasts.length > 0 && (
        <div className="toast-container">
          <div className="toast-stack">
            {toasts.map((toast) => (
              <Toast
                key={toast.id}
                toast={toast}
                onClose={removeToast}
              />
            ))}
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
};

export default ToastContext;